/**
 * CapturePageBuilder — the editor + live preview of the user's landing page.
 *
 * Two modes, toggled by the user:
 *   - Aperçu  : full live preview inside a browser-chrome mockup (desktop / mobile)
 *   - Éditeur : structured panel — toggle/reorder sections + edit fields per section
 *
 * Each section has its own ✨ regenerate button (one section call to Claude),
 * and a global "✨ Régénérer la landing" button to redo the whole thing in one shot.
 */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  Loader2,
  Check,
  Sparkles,
  Smartphone,
  Monitor,
  Copy,
  Eye,
  Pencil,
  X,
  Lock,
  Star,
  Plus,
  Minus,
  GripVertical,
  ChevronDown,
  TrendingUp,
  ShieldCheck,
  Heart,
  Target,
  Zap,
  Users,
  CheckCircle2,
  Award,
  Rocket,
  Compass,
  Trash2,
} from 'lucide-react';
import {
  type BrandKit,
  type BrandPalette,
  type CapturePage,
  type SectionType,
  type LandingSection,
  type RichBenefit,
  type Testimonial,
  type FaqItem,
  type HeroMedia,
  type VariantB,
  DEFAULT_SECTIONS,
} from '@/hooks/useIdentity';
import { useAuth } from '@/hooks/useAuth';

const ICON_MAP = {
  TrendingUp,
  ShieldCheck,
  Heart,
  Target,
  Zap,
  Users,
  CheckCircle2,
  Award,
  Rocket,
  Compass,
  Sparkles,
} as const;

const SECTION_KEYS: Record<SectionType, string> = {
  hero: 'identity.sections.hero',
  benefits_rich: 'identity.sections.benefits_rich',
  testimonials: 'identity.sections.testimonials',
  before_after: 'identity.sections.before_after',
  faq: 'identity.sections.faq',
  cta_bottom: 'identity.sections.cta_bottom',
};

interface Props {
  kit: BrandKit;
  paletteIndex: number;
  onSave: (cp: CapturePage) => void;
  saving: boolean;
  onRegenerateAll: () => void;
  onRegenerateSection: (type: SectionType) => void;
  regeneratingAll: boolean;
  regeneratingSection: SectionType | null;
}

export function CapturePageBuilder({
  kit,
  paletteIndex,
  onSave,
  saving,
  onRegenerateAll,
  onRegenerateSection,
  regeneratingAll,
  regeneratingSection,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [mode, setMode] = useState<'preview' | 'editor'>('preview');
  const [viewport, setViewport] = useState<'desktop' | 'mobile'>('desktop');
  const [fullscreen, setFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  /** Which A/B variant is currently rendered in the preview. Real visitors get
   *  a 50/50 split via a `nia_ab` cookie set client-side on first view of the
   *  public landing. Inside the builder this is a manual toggle so the user can
   *  see both variants. */
  const [activeVariant, setActiveVariant] = useState<'a' | 'b'>('a');

  // Local working copy — edits are buffered, saved on click.
  const [cp, setCp] = useState<CapturePage>(kit.capture_page);
  useEffect(() => setCp(kit.capture_page), [kit.capture_page]);
  const dirty = useMemo(
    () => JSON.stringify(cp) !== JSON.stringify(kit.capture_page),
    [cp, kit.capture_page],
  );

  const palette = kit.palettes[paletteIndex] ?? kit.palettes[0];

  const slug = (user?.full_name ?? user?.display_name ?? 'votre-nom')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);
  const url = `nia.io/u/${slug}`;
  const firstName = user?.full_name?.split(' ')[0] ?? 'Vous';

  function copyLink(): void {
    void navigator.clipboard.writeText(`https://${url}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {/* Mode toggle */}
          <div className="inline-flex rounded-full border border-ink/[0.10] bg-white/65 p-1 backdrop-blur">
            <button
              onClick={() => setMode('preview')}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-700 transition ${
                mode === 'preview'
                  ? 'bg-gradient-to-br from-blue to-violet text-white shadow-[0_4px_12px_-4px_rgba(168,85,247,0.5)]'
                  : 'text-ink/60 hover:text-ink'
              }`}
            >
              <Eye size={12} /> Aperçu
            </button>
            <button
              onClick={() => setMode('editor')}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-700 transition ${
                mode === 'editor'
                  ? 'bg-gradient-to-br from-violet to-pink text-white shadow-[0_4px_12px_-4px_rgba(236,72,153,0.5)]'
                  : 'text-ink/60 hover:text-ink'
              }`}
            >
              <Pencil size={12} /> Éditer
            </button>
          </div>
          {/* Viewport switcher — only useful in Preview */}
          {mode === 'preview' && (
            <div className="inline-flex rounded-full border border-ink/[0.10] bg-white/65 p-1 backdrop-blur">
              <button
                onClick={() => setViewport('desktop')}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-700 transition ${
                  viewport === 'desktop'
                    ? 'bg-ink text-white'
                    : 'text-ink/60 hover:text-ink'
                }`}
              >
                <Monitor size={12} /> Desktop
              </button>
              <button
                onClick={() => setViewport('mobile')}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-700 transition ${
                  viewport === 'mobile'
                    ? 'bg-ink text-white'
                    : 'text-ink/60 hover:text-ink'
                }`}
              >
                <Smartphone size={12} /> Mobile
              </button>
            </div>
          )}
          {/* A/B variant toggle (only if variant_b enabled) */}
          {mode === 'preview' && cp.variant_b?.enabled && (
            <div className="inline-flex rounded-full border border-violet/40 bg-gradient-to-r from-violet/10 to-pink/10 p-1 backdrop-blur">
              <button
                onClick={() => setActiveVariant('a')}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-800 transition ${
                  activeVariant === 'a' ? 'bg-violet text-white shadow' : 'text-violet/80 hover:text-violet'
                }`}
                title={t("capture.variantA")}
              >
                A
              </button>
              <button
                onClick={() => setActiveVariant('b')}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-800 transition ${
                  activeVariant === 'b' ? 'bg-pink text-white shadow' : 'text-pink/80 hover:text-pink'
                }`}
                title={t("capture.variantB")}
              >
                B
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Magic regenerate all */}
          <button
            onClick={onRegenerateAll}
            disabled={regeneratingAll}
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue via-violet to-pink px-4 py-1.5 text-[12px] font-700 text-white shadow-[0_8px_20px_-8px_rgba(168,85,247,0.55)] transition hover:scale-[1.02] disabled:opacity-60"
            title={t("capture.regenerateLandingTooltip")}
          >
            {regeneratingAll ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {regeneratingAll ? t('identity.regenerating') : t('identity.actions.regenerateLanding')}
          </button>
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/[0.10] bg-white/85 px-3 py-1.5 text-[11.5px] font-700 text-ink/70 transition hover:border-pink/40 hover:text-pink"
            title={t("capture.copyLinkTooltip")}
          >
            {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
            {copied ? 'Copié !' : url}
          </button>
          {mode === 'preview' && (
            <button
              onClick={() => setFullscreen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink/[0.10] bg-white/85 px-3 py-1.5 text-[11.5px] font-700 text-ink/70 transition hover:border-violet/40 hover:text-violet"
            >
              <Eye size={12} /> Plein écran
            </button>
          )}
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {mode === 'preview' ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            <BrowserMockup viewport={viewport} url={url}>
              <LandingRender
                cp={cp}
                palette={palette}
                firstName={firstName}
                typoTitle={kit.typos.title}
                variant={viewport}
                activeVariant={activeVariant}
              />
            </BrowserMockup>
          </motion.div>
        ) : (
          <motion.div
            key="editor"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            <SectionEditor
              cp={cp}
              setCp={setCp}
              onRegenerateSection={onRegenerateSection}
              regeneratingSection={regeneratingSection}
              palette={palette}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save bar */}
      {mode === 'editor' && (
        <div className="mt-5 flex items-center gap-2.5">
          <button
            onClick={() => onSave(cp)}
            disabled={saving || !dirty}
            className="btn-primary px-5 py-2.5 text-[13px] disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Enregistrer
          </button>
          {dirty && (
            <button
              onClick={() => setCp(kit.capture_page)}
              className="btn-ghost px-5 py-2.5 text-[13px]"
            >
              Annuler
            </button>
          )}
          <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/40">
            {(cp.sections ?? DEFAULT_SECTIONS).filter((s) => s.enabled).length} sections actives
          </span>
        </div>
      )}

      {/* Fullscreen */}
      <AnimatePresence>
        {fullscreen && (
          <FullscreenPreview onClose={() => setFullscreen(false)}>
            <LandingRender
              cp={cp}
              palette={palette}
              firstName={firstName}
              typoTitle={kit.typos.title}
              variant="desktop"
              dense
              activeVariant={activeVariant}
            />
          </FullscreenPreview>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section editor — toggle, reorder via drag, edit fields per section
// ─────────────────────────────────────────────────────────────────────────────

function SectionEditor({
  cp,
  setCp,
  onRegenerateSection,
  regeneratingSection,
  palette,
}: {
  cp: CapturePage;
  setCp: (cp: CapturePage) => void;
  onRegenerateSection: (type: SectionType) => void;
  regeneratingSection: SectionType | null;
  palette: BrandPalette | undefined;
}) {
  const sections = cp.sections.length > 0 ? cp.sections : DEFAULT_SECTIONS;

  function toggleSection(type: SectionType): void {
    setCp({
      ...cp,
      sections: sections.map((s) => (s.type === type ? { ...s, enabled: !s.enabled } : s)),
    });
  }

  function reorderSections(next: LandingSection[]): void {
    setCp({ ...cp, sections: next });
  }

  return (
    <Reorder.Group
      axis="y"
      values={sections}
      onReorder={reorderSections}
      className="space-y-2.5"
    >
      {sections.map((section) => (
        <Reorder.Item
          key={section.type}
          value={section}
          className="rounded-2xl border border-ink/[0.08] bg-white/85 backdrop-blur"
        >
          <SectionRow
            section={section}
            cp={cp}
            setCp={setCp}
            onToggle={() => toggleSection(section.type)}
            onRegenerate={() => onRegenerateSection(section.type)}
            regenerating={regeneratingSection === section.type}
            palette={palette}
          />
        </Reorder.Item>
      ))}
    </Reorder.Group>
  );
}

function SectionRow({
  section,
  cp,
  setCp,
  onToggle,
  onRegenerate,
  regenerating,
  palette,
}: {
  section: LandingSection;
  cp: CapturePage;
  setCp: (cp: CapturePage) => void;
  onToggle: () => void;
  onRegenerate: () => void;
  regenerating: boolean;
  palette: BrandPalette | undefined;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(section.type === 'hero');
  return (
    <div>
      {/* Row header */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <GripVertical size={14} className="cursor-grab text-ink/30 hover:text-ink/70" />
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <ChevronDown
            size={14}
            className={`text-ink/40 transition ${open ? '' : '-rotate-90'}`}
          />
          <span className="font-display text-[13px] font-800 tracking-[-0.015em]">
            {t(SECTION_KEYS[section.type])}
          </span>
          <span
            className={`ml-2 inline-flex h-1.5 w-1.5 rounded-full ${
              section.enabled ? 'bg-emerald-500' : 'bg-ink/20'
            }`}
          />
        </button>
        <button
          onClick={onRegenerate}
          disabled={regenerating || !section.enabled}
          className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet/15 to-pink/15 px-2.5 py-1 text-[10.5px] font-700 text-violet transition hover:from-violet/25 hover:to-pink/25 disabled:opacity-40"
        >
          {regenerating ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
          Sugg.
        </button>
        <button
          onClick={onToggle}
          disabled={section.type === 'hero'}
          className={`inline-flex h-6 w-10 items-center rounded-full p-0.5 transition ${
            section.enabled ? 'bg-gradient-to-r from-blue to-violet' : 'bg-ink/15'
          } ${section.type === 'hero' ? 'cursor-not-allowed opacity-50' : ''}`}
          title={section.type === 'hero' ? t('identity.sections.lockedHero') : ''}
        >
          <span
            className={`block h-5 w-5 rounded-full bg-white shadow transition ${
              section.enabled ? 'translate-x-4' : ''
            }`}
          />
        </button>
      </div>

      {/* Section body — editable fields */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 border-t border-ink/[0.06] bg-ink/[0.015] px-4 py-4">
              <SectionFields
                type={section.type}
                cp={cp}
                setCp={setCp}
                palette={palette}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionFields({
  type,
  cp,
  setCp,
  palette,
}: {
  type: SectionType;
  cp: CapturePage;
  setCp: (cp: CapturePage) => void;
  palette: BrandPalette | undefined;
}) {
  const { t } = useTranslation();
  if (type === 'hero') {
    return (
      <>
        <HeroMediaEditor
          media={cp.hero_media}
          onChange={(hero_media) => setCp({ ...cp, hero_media })}
        />
        <FieldInput
          label="Accroche"
          value={cp.headline}
          onChange={(v) => setCp({ ...cp, headline: v })}
        />
        <FieldInput
          label="Promesse"
          value={cp.promise}
          onChange={(v) => setCp({ ...cp, promise: v })}
          textarea
        />
        <BenefitsListEditor
          label={t('identity.fields.benefitsShort', { max: 3 })}
          items={cp.benefits}
          onChange={(benefits) => setCp({ ...cp, benefits })}
          max={3}
        />
        <FieldInput
          label="Texte du bouton"
          value={cp.cta}
          onChange={(v) => setCp({ ...cp, cta: v })}
        />
        <VariantBEditor
          variant={cp.variant_b}
          fallbackHeadline={cp.headline}
          fallbackCta={cp.cta}
          onChange={(variant_b) => setCp({ ...cp, variant_b })}
        />
      </>
    );
  }
  if (type === 'benefits_rich') {
    return (
      <RichBenefitsEditor
        items={cp.benefits_rich ?? []}
        onChange={(benefits_rich) => setCp({ ...cp, benefits_rich })}
        palette={palette}
      />
    );
  }
  if (type === 'testimonials') {
    return (
      <TestimonialsEditor
        items={cp.testimonials ?? []}
        onChange={(testimonials) => setCp({ ...cp, testimonials })}
      />
    );
  }
  if (type === 'before_after') {
    const ba = cp.before_after ?? { before: '', after: '' };
    return (
      <>
        <FieldInput
          label="Avant — frustration actuelle"
          value={ba.before}
          onChange={(v) => setCp({ ...cp, before_after: { ...ba, before: v } })}
          textarea
        />
        <FieldInput
          label={t('identity.fields.afterLabel')}
          value={ba.after}
          onChange={(v) => setCp({ ...cp, before_after: { ...ba, after: v } })}
          textarea
        />
      </>
    );
  }
  if (type === 'faq') {
    return (
      <FaqEditor
        items={cp.faq ?? []}
        onChange={(faq) => setCp({ ...cp, faq })}
      />
    );
  }
  if (type === 'cta_bottom') {
    const cb = cp.cta_bottom ?? { headline: '', urgency: '', cta: '' };
    return (
      <>
        <FieldInput
          label="Accroche finale"
          value={cb.headline}
          onChange={(v) => setCp({ ...cp, cta_bottom: { ...cb, headline: v } })}
        />
        <FieldInput
          label={t('identity.fields.urgency')}
          value={cb.urgency}
          onChange={(v) => setCp({ ...cp, cta_bottom: { ...cb, urgency: v } })}
        />
        <FieldInput
          label="Texte du bouton"
          value={cb.cta}
          onChange={(v) => setCp({ ...cp, cta_bottom: { ...cb, cta: v } })}
        />
      </>
    );
  }
  return null;
}

// ── Generic editors ──────────────────────────────────────────────────────────

function FieldInput({
  label,
  value,
  onChange,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-600 uppercase tracking-[0.1em] text-ink/55">
        {label}
      </span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full resize-none rounded-xl border border-ink/[0.10] bg-white px-3 py-2 text-[13px] outline-none focus:border-pink/60 focus:ring-2 focus:ring-pink/15"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-ink/[0.10] bg-white px-3 py-2 text-[13px] outline-none focus:border-pink/60 focus:ring-2 focus:ring-pink/15"
        />
      )}
    </label>
  );
}

function BenefitsListEditor({
  label,
  items,
  onChange,
  max,
}: {
  label: string;
  items: string[];
  onChange: (v: string[]) => void;
  max: number;
}) {
  return (
    <div>
      <span className="mb-1 block text-[11px] font-600 uppercase tracking-[0.1em] text-ink/55">
        {label}
      </span>
      <div className="space-y-2">
        {items.map((b, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={b}
              maxLength={120}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
              className="flex-1 rounded-xl border border-ink/[0.10] bg-white px-3 py-2 text-[13px] outline-none focus:border-pink/60 focus:ring-2 focus:ring-pink/15"
            />
            <button
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="rounded-xl bg-ink/[0.05] px-2.5 text-[12px] font-600 text-ink/55 transition hover:bg-pink/10 hover:text-pink"
            >
              ✕
            </button>
          </div>
        ))}
        {items.length < max && (
          <button
            onClick={() => onChange([...items, ''])}
            className="inline-flex items-center gap-1 text-[12px] font-700 text-pink transition hover:underline"
          >
            <Plus size={12} /> Ajouter
          </button>
        )}
      </div>
    </div>
  );
}

const AVAILABLE_ICONS = [
  'TrendingUp',
  'Sparkles',
  'ShieldCheck',
  'Heart',
  'Target',
  'Zap',
  'Users',
  'CheckCircle2',
  'Award',
  'Rocket',
  'Compass',
];

function RichBenefitsEditor({
  items,
  onChange,
  palette,
}: {
  items: RichBenefit[];
  onChange: (v: RichBenefit[]) => void;
  palette: BrandPalette | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <span className="mb-1 block text-[11px] font-600 uppercase tracking-[0.1em] text-ink/55">
        {t('identity.fields.richMax')}
      </span>
      <div className="space-y-3">
        {items.map((b, i) => {
          const Icon = (ICON_MAP[b.icon as keyof typeof ICON_MAP] ?? Sparkles) as typeof Sparkles;
          return (
            <div key={i} className="rounded-xl border border-ink/[0.08] bg-white p-3">
              <div className="mb-2 flex items-center gap-2">
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-white"
                  style={{
                    background: `linear-gradient(135deg, ${palette?.colors[0] ?? '#3b82f6'}, ${palette?.colors[1] ?? '#ec4899'})`,
                  }}
                >
                  <Icon size={15} />
                </span>
                <select
                  value={b.icon}
                  onChange={(e) => {
                    const next = [...items];
                    next[i] = { ...b, icon: e.target.value };
                    onChange(next);
                  }}
                  className="flex-1 rounded-lg border border-ink/[0.10] bg-white px-2 py-1 text-[12px] font-600"
                >
                  {AVAILABLE_ICONS.map((ic) => (
                    <option key={ic} value={ic}>
                      {ic}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                  className="rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] text-ink/55 transition hover:bg-pink/10 hover:text-pink"
                >
                  <Trash2 size={11} />
                </button>
              </div>
              <input
                value={b.title}
                placeholder={t("capture.benefitTitlePlaceholder")}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...b, title: e.target.value };
                  onChange(next);
                }}
                className="mb-2 w-full rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[13px] font-700 outline-none focus:border-pink/60"
              />
              <textarea
                value={b.text}
                placeholder={t("capture.benefitTextPlaceholder")}
                rows={2}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...b, text: e.target.value };
                  onChange(next);
                }}
                className="w-full resize-none rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[12.5px] leading-[1.5] outline-none focus:border-pink/60"
              />
            </div>
          );
        })}
        {items.length < 4 && (
          <button
            onClick={() =>
              onChange([
                ...items,
                { icon: 'Sparkles', title: t('identity.fields.title'), text: '' },
              ])
            }
            className="inline-flex items-center gap-1 text-[12px] font-700 text-pink transition hover:underline"
          >
            <Plus size={12} /> Ajouter un bénéfice
          </button>
        )}
      </div>
    </div>
  );
}

const AVATAR_OPTIONS = ['🌿', '💎', '🚀', '🔥', '⭐', '🌊', '☀️', '🌙', '✨', '🎯'];

function TestimonialsEditor({
  items,
  onChange,
}: {
  items: Testimonial[];
  onChange: (v: Testimonial[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <span className="mb-1 block text-[11px] font-600 uppercase tracking-[0.1em] text-ink/55">
        {t('identity.fields.testimoniaMax')}
      </span>
      <div className="space-y-3">
        {items.map((tm, i) => (
          <div key={i} className="rounded-xl border border-ink/[0.08] bg-white p-3">
            <div className="mb-2 flex items-center gap-2">
              {tm.avatar_url ? (
                <img
                  src={tm.avatar_url}
                  alt={tm.name}
                  className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-ink/10"
                  title={t('capture.portraitGenerated')}
                />
              ) : (
                <select
                  value={tm.avatar ?? '🌿'}
                  onChange={(e) => {
                    const next = [...items];
                    next[i] = { ...tm, avatar: e.target.value };
                    onChange(next);
                  }}
                  className="rounded-lg border border-ink/[0.10] bg-white px-2 py-1 text-[14px]"
                  title={t('capture.avatarEmojiHint')}
                >
                  {AVATAR_OPTIONS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              )}
              <input
                value={tm.name}
                placeholder={t('identity.fields.fullName') as string}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...tm, name: e.target.value };
                  onChange(next);
                }}
                className="flex-1 rounded-lg border border-ink/[0.10] bg-white px-2 py-1 text-[12.5px] font-700 outline-none focus:border-pink/60"
              />
              <button
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] text-ink/55 transition hover:bg-pink/10 hover:text-pink"
              >
                <Trash2 size={11} />
              </button>
            </div>
            <input
              value={tm.role}
              placeholder={t('identity.fields.role') as string}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...tm, role: e.target.value };
                onChange(next);
              }}
              className="mb-2 w-full rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[12.5px] text-ink/70 outline-none focus:border-pink/60"
            />
            <textarea
              value={tm.quote}
              placeholder={t('identity.fields.quote') as string}
              rows={2}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...tm, quote: e.target.value };
                onChange(next);
              }}
              className="mb-2 w-full resize-none rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[12.5px] italic leading-[1.5] outline-none focus:border-pink/60"
            />
            <input
              value={tm.result}
              placeholder={t('identity.fields.result') as string}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...tm, result: e.target.value };
                onChange(next);
              }}
              className="w-full rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[12.5px] font-700 text-pink outline-none focus:border-pink/60"
            />
          </div>
        ))}
        {items.length < 4 && (
          <button
            onClick={() =>
              onChange([
                ...items,
                { name: '', role: '', quote: '', result: '', avatar: '🌿' },
              ])
            }
            className="inline-flex items-center gap-1 text-[12px] font-700 text-pink transition hover:underline"
          >
            <Plus size={12} /> {t('identity.fields.addTestimonial')}
          </button>
        )}
      </div>
    </div>
  );
}

function FaqEditor({
  items,
  onChange,
}: {
  items: FaqItem[];
  onChange: (v: FaqItem[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <span className="mb-1 block text-[11px] font-600 uppercase tracking-[0.1em] text-ink/55">
        {t('identity.fields.faqMax')}
      </span>
      <div className="space-y-2">
        {items.map((f, i) => (
          <div key={i} className="rounded-xl border border-ink/[0.08] bg-white p-3">
            <div className="mb-1.5 flex items-start gap-2">
              <input
                value={f.question}
                placeholder={t('capture.questionPlaceholder')}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...f, question: e.target.value };
                  onChange(next);
                }}
                className="flex-1 rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[12.5px] font-700 outline-none focus:border-pink/60"
              />
              <button
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] text-ink/55 transition hover:bg-pink/10 hover:text-pink"
              >
                <Trash2 size={11} />
              </button>
            </div>
            <textarea
              value={f.answer}
              placeholder={t("capture.faqAnswerPlaceholder")}
              rows={2}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...f, answer: e.target.value };
                onChange(next);
              }}
              className="w-full resize-none rounded-lg border border-ink/[0.08] bg-white px-2.5 py-1.5 text-[12.5px] leading-[1.5] outline-none focus:border-pink/60"
            />
          </div>
        ))}
        {items.length < 8 && (
          <button
            onClick={() => onChange([...items, { question: '', answer: '' }])}
            className="inline-flex items-center gap-1 text-[12px] font-700 text-pink transition hover:underline"
          >
            <Plus size={12} /> Ajouter une question
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live preview — browser chrome + landing content rendering all sections
// ─────────────────────────────────────────────────────────────────────────────

function BrowserMockup({
  viewport,
  url,
  children,
}: {
  viewport: 'desktop' | 'mobile';
  url: string;
  children: React.ReactNode;
}) {
  if (viewport === 'mobile') {
    return (
      <div className="mx-auto w-full max-w-[360px]">
        <div className="relative rounded-[40px] border-[10px] border-ink/85 bg-ink/85 shadow-[0_30px_60px_-20px_rgba(10,18,38,0.5),0_10px_24px_-12px_rgba(168,139,250,0.45)]">
          <div className="absolute left-1/2 top-0 z-10 flex h-5 w-28 -translate-x-1/2 items-center justify-center rounded-b-2xl bg-ink/85">
            <span className="h-1 w-12 rounded-full bg-ink/40" />
          </div>
          <div className="relative overflow-hidden rounded-[28px] bg-white">
            <div className="flex items-center justify-between bg-white px-5 pb-1.5 pt-3 text-[10px] font-700 text-ink">
              <span>9:41</span>
              <span className="h-2.5 w-3.5 rounded-sm border border-ink" />
            </div>
            <div className="mx-3 mb-2 flex items-center gap-1.5 rounded-full bg-ink/[0.05] px-3 py-1">
              <Lock size={9} className="text-ink/55" />
              <span className="truncate font-mono text-[10px] text-ink/70">{url}</span>
            </div>
            <div className="max-h-[640px] overflow-y-auto">{children}</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-ink/[0.10] bg-ink/[0.04] shadow-[0_24px_60px_-24px_rgba(10,18,38,0.40),0_10px_24px_-12px_rgba(168,139,250,0.30)]">
      <div className="flex items-center gap-2 border-b border-ink/[0.08] bg-gradient-to-b from-white/95 to-white/85 px-3.5 py-2.5">
        <div className="flex shrink-0 gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </div>
        <div className="mx-auto flex w-full max-w-[420px] items-center gap-2 rounded-full border border-ink/[0.08] bg-white px-3 py-1 shadow-inner">
          <Lock size={10} className="shrink-0 text-emerald-500" />
          <span className="truncate font-mono text-[11px] text-ink/70">{url}</span>
          <Star size={9} className="ml-auto text-ink/40" />
        </div>
        <div className="w-12 shrink-0" />
      </div>
      <div className="max-h-[760px] overflow-y-auto bg-white">{children}</div>
    </div>
  );
}

function FullscreenPreview({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-night/85 p-4 backdrop-blur-md sm:p-8"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[92vh] w-full max-w-[1100px] overflow-hidden rounded-3xl border border-white/20 bg-white shadow-[0_40px_80px_-30px_rgba(10,18,38,0.6)]"
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-night/65 text-white backdrop-blur transition hover:bg-night/85"
          aria-label={t('common.close')}
        >
          <X size={16} />
        </button>
        <div className="max-h-[92vh] overflow-y-auto">{children}</div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Landing content — renders each enabled section, in user-chosen order
// ─────────────────────────────────────────────────────────────────────────────

function LandingRender({
  cp,
  palette,
  firstName,
  typoTitle,
  variant,
  dense = false,
  activeVariant = 'a',
}: {
  cp: CapturePage;
  palette: BrandPalette | undefined;
  firstName: string;
  typoTitle: string;
  variant: 'desktop' | 'mobile';
  dense?: boolean;
  activeVariant?: 'a' | 'b';
}) {
  const c0 = palette?.colors[0] ?? '#3b82f6';
  const c1 = palette?.colors[1] ?? '#ec4899';
  const c2 = palette?.colors[2] ?? c1;
  const sections = cp.sections.length > 0 ? cp.sections : DEFAULT_SECTIONS;
  const isMobile = variant === 'mobile';

  return (
    <div className="relative bg-white">
      {sections
        .filter((s) => s.enabled)
        .map((s) => {
          switch (s.type) {
            case 'hero':
              return (
                <HeroSection
                  key="hero"
                  cp={cp}
                  c0={c0}
                  c1={c1}
                  c2={c2}
                  firstName={firstName}
                  typoTitle={typoTitle}
                  isMobile={isMobile}
                  dense={dense}
                  activeVariant={activeVariant}
                />
              );
            case 'benefits_rich':
              return (
                <BenefitsRichSection
                  key="benefits_rich"
                  items={cp.benefits_rich ?? []}
                  c0={c0}
                  c1={c1}
                  isMobile={isMobile}
                />
              );
            case 'testimonials':
              return (
                <TestimonialsSection
                  key="testimonials"
                  items={cp.testimonials ?? []}
                  c0={c0}
                  c1={c1}
                  isMobile={isMobile}
                />
              );
            case 'before_after':
              return (
                <BeforeAfterSection
                  key="before_after"
                  ba={cp.before_after ?? { before: '', after: '' }}
                  c1={c1}
                  isMobile={isMobile}
                />
              );
            case 'faq':
              return (
                <FaqSection
                  key="faq"
                  items={cp.faq ?? []}
                  c1={c1}
                  isMobile={isMobile}
                />
              );
            case 'cta_bottom':
              return (
                <CtaBottomSection
                  key="cta_bottom"
                  cta={cp.cta_bottom ?? { headline: '', urgency: '', cta: '' }}
                  c0={c0}
                  c1={c1}
                  isMobile={isMobile}
                />
              );
            default:
              return null;
          }
        })}
    </div>
  );
}

function HeroSection({
  cp,
  c0,
  c1,
  c2,
  firstName,
  typoTitle,
  isMobile,
  dense,
  activeVariant,
}: {
  cp: CapturePage;
  c0: string;
  c1: string;
  c2: string;
  firstName: string;
  typoTitle: string;
  isMobile: boolean;
  dense?: boolean;
  activeVariant: 'a' | 'b';
}) {
  const { t } = useTranslation();
  const useB = activeVariant === 'b' && cp.variant_b?.enabled;
  const heroHeadline = useB && cp.variant_b ? cp.variant_b.headline || cp.headline : cp.headline;
  const heroCta = useB && cp.variant_b ? cp.variant_b.cta || cp.cta : cp.cta;
  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: `radial-gradient(ellipse at 30% 0%, ${c0}, transparent 55%), radial-gradient(ellipse at 80% 100%, ${c2}, transparent 60%), ${c1}`,
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div
        className={`relative mx-auto ${
          isMobile ? 'px-5 py-9' : dense ? 'px-10 py-14' : 'px-8 py-14 sm:px-12 sm:py-18'
        } ${isMobile ? '' : 'max-w-[660px]'} text-center`}
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-white/95 ring-2 ring-white/40 backdrop-blur">
          <span className="font-display text-[22px] font-800 leading-none" style={{ color: c1 }}>
            {firstName.charAt(0)}
          </span>
        </div>
        <span
          className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-white"
          style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}
        >
          <span className="h-1 w-1 rounded-full bg-white" /> {typoTitle}
        </span>
        {cp.hero_media && cp.hero_media.url && cp.hero_media.provider !== 'none' && (
          <div className={`mx-auto mb-6 ${isMobile ? 'max-w-full' : 'max-w-[520px]'}`}>
            <VideoEmbed media={cp.hero_media} />
          </div>
        )}
        <h4
          className={`font-display font-800 leading-[1.08] tracking-[-0.025em] text-white ${
            isMobile ? 'text-[22px]' : dense ? 'text-[40px]' : 'text-[clamp(24px,3.6vw,40px)]'
          }`}
        >
          {heroHeadline || `Hello ${firstName}, voici votre promesse.`}
        </h4>
        <p
          className={`mx-auto mt-3.5 max-w-[540px] leading-[1.6] text-white/90 ${
            isMobile ? 'text-[13px]' : 'text-[15px]'
          }`}
        >
          {cp.promise || 'Votre promesse de valeur.'}
        </p>
        {cp.benefits.length > 0 && (
          <ul className="mx-auto mt-6 flex max-w-[420px] flex-col gap-2 text-left">
            {cp.benefits.map((b, i) => (
              <li
                key={i}
                className="flex items-center gap-2.5 rounded-xl border border-white/15 px-3.5 py-2.5 text-[13px] font-600 text-white backdrop-blur"
                style={{ background: 'rgba(255,255,255,0.10)' }}
              >
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                  style={{ background: '#fff' }}
                >
                  <Check size={11} style={{ color: c1 }} />
                </span>
                {b}
              </li>
            ))}
          </ul>
        )}
        <form
          onSubmit={(e) => e.preventDefault()}
          className={`mx-auto mt-7 flex w-full max-w-[420px] ${
            isMobile ? 'flex-col gap-2' : 'flex-row gap-2'
          }`}
        >
          <input
            type="email"
            placeholder={t("capture.emailPlaceholderShort")}
            disabled
            className="flex-1 rounded-full border border-white/30 bg-white/15 px-4 py-3 text-[13px] text-white placeholder:text-white/55 backdrop-blur"
          />
          <button
            type="submit"
            className="rounded-full px-5 py-3 text-[13px] font-800 shadow-lg transition hover:scale-[1.02]"
            style={{
              background: '#fff',
              color: c1,
              boxShadow: '0 12px 30px -10px rgba(0,0,0,0.35)',
            }}
          >
            {heroCta || 'Je découvre'}
          </button>
        </form>
        <p className="mt-3.5 inline-flex items-center gap-1.5 text-[10.5px] font-600 text-white/65">
          <Lock size={9} /> 100% gratuit · pas de spam, désabonnement en 1 clic
        </p>
      </div>
    </div>
  );
}

function BenefitsRichSection({
  items,
  c0,
  c1,
  isMobile,
}: {
  items: RichBenefit[];
  c0: string;
  c1: string;
  isMobile: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section className={`bg-white ${isMobile ? 'px-5 py-9' : 'px-8 py-14'}`}>
      <div className="mx-auto max-w-[860px]">
        <p
          className="mb-2 font-mono text-[10px] font-700 uppercase tracking-[0.18em]"
          style={{ color: c1 }}
        >
          {t('identity.landing.benefitsKicker')}
        </p>
        <h3 className="mb-6 font-display text-[clamp(22px,3vw,32px)] font-800 leading-[1.1] tracking-[-0.025em] text-ink">
          Trois leviers concrets, pas de promesses vides.
        </h3>
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'sm:grid-cols-3'}`}>
          {items.map((b, i) => {
            const Icon = (ICON_MAP[b.icon as keyof typeof ICON_MAP] ?? Sparkles) as typeof Sparkles;
            return (
              <article
                key={i}
                className="relative overflow-hidden rounded-2xl border border-ink/[0.08] bg-gradient-to-br from-white to-ink/[0.015] p-5 transition hover:-translate-y-1 hover:shadow-lg"
              >
                <span
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${c0}, ${c1})` }}
                >
                  <Icon size={18} />
                </span>
                <h4 className="mt-3.5 font-display text-[16px] font-800 leading-[1.2] tracking-[-0.015em] text-ink">
                  {b.title}
                </h4>
                <p className="mt-1.5 text-[13px] leading-[1.55] text-ink/70">{b.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({
  items,
  c0,
  c1,
  isMobile,
}: {
  items: Testimonial[];
  c0: string;
  c1: string;
  isMobile: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section
      className={`relative ${isMobile ? 'px-5 py-9' : 'px-8 py-14'}`}
      style={{ background: `linear-gradient(180deg, #fafafa, #f4f4f5)` }}
    >
      <div className="mx-auto max-w-[860px]">
        <p
          className="mb-2 font-mono text-[10px] font-700 uppercase tracking-[0.18em]"
          style={{ color: c1 }}
        >
          {t('identity.landing.testimonialsKicker')}
        </p>
        <h3 className="mb-6 font-display text-[clamp(22px,3vw,32px)] font-800 leading-[1.1] tracking-[-0.025em] text-ink">
          {t('identity.landing.testimonialsHeading')}
        </h3>
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'sm:grid-cols-3'}`}>
          {items.map((tm, i) => (
            <article
              key={i}
              className="relative flex h-full flex-col rounded-2xl border border-ink/[0.08] bg-white p-5 shadow-[0_8px_24px_-12px_rgba(10,18,38,0.10)]"
            >
              <div className="mb-3 flex items-center gap-3">
                {tm.avatar_url ? (
                  <img
                    src={tm.avatar_url}
                    alt={tm.name}
                    className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-white shadow-[0_4px_10px_-3px_rgba(10,18,38,0.20)]"
                  />
                ) : (
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[20px] ring-2 ring-white"
                    style={{ background: `linear-gradient(135deg, ${c0}33, ${c1}33)` }}
                  >
                    {tm.avatar ?? '🌿'}
                  </span>
                )}
                <div>
                  <p className="font-display text-[13px] font-800 leading-tight tracking-[-0.01em] text-ink">
                    {tm.name}
                  </p>
                  <p className="text-[11px] text-ink/55">{tm.role}</p>
                </div>
              </div>
              <p className="flex-1 text-[13px] italic leading-[1.55] text-ink/75">« {tm.quote} »</p>
              <span
                className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 font-mono text-[10px] font-700"
                style={{ background: `${c1}15`, color: c1 }}
              >
                <TrendingUp size={10} /> {tm.result}
              </span>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeforeAfterSection({
  ba,
  c1,
  isMobile,
}: {
  ba: { before: string; after: string };
  c1: string;
  isMobile: boolean;
}) {
  const { t } = useTranslation();
  if (!ba.before && !ba.after) return null;
  return (
    <section className={`bg-white ${isMobile ? 'px-5 py-9' : 'px-8 py-14'}`}>
      <div className="mx-auto max-w-[860px]">
        <p
          className="mb-2 font-mono text-[10px] font-700 uppercase tracking-[0.18em]"
          style={{ color: c1 }}
        >
          La transformation
        </p>
        <h3 className="mb-6 font-display text-[clamp(22px,3vw,32px)] font-800 leading-[1.1] tracking-[-0.025em] text-ink">
          {t('identity.landing.beforeAfterHeading')}
        </h3>
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
          <article className="rounded-2xl border border-ink/[0.08] bg-ink/[0.02] p-5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/[0.06] px-2.5 py-1 font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/55">
              <Minus size={9} /> Avant
            </span>
            <p className="mt-3 text-[14px] leading-[1.6] text-ink/70">{ba.before}</p>
          </article>
          <article
            className="rounded-2xl border p-5"
            style={{ borderColor: `${c1}30`, background: `${c1}06` }}
          >
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-white"
              style={{ background: c1 }}
            >
              <Plus size={9} /> Après
            </span>
            <p className="mt-3 text-[14px] font-600 leading-[1.6] text-ink">{ba.after}</p>
          </article>
        </div>
      </div>
    </section>
  );
}

function FaqSection({
  items,
  c1,
  isMobile,
}: {
  items: FaqItem[];
  c1: string;
  isMobile: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section
      className={`${isMobile ? 'px-5 py-9' : 'px-8 py-14'}`}
      style={{ background: '#fafafa' }}
    >
      <div className="mx-auto max-w-[700px]">
        <p
          className="mb-2 font-mono text-[10px] font-700 uppercase tracking-[0.18em]"
          style={{ color: c1 }}
        >
          On répond
        </p>
        <h3 className="mb-6 font-display text-[clamp(22px,3vw,32px)] font-800 leading-[1.1] tracking-[-0.025em] text-ink">
          {t('identity.landing.faqHeading')}
        </h3>
        <div className="space-y-2.5">
          {items.map((f, i) => (
            <FaqRow key={i} item={f} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-ink/[0.08] bg-white">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
      >
        <span className="font-display text-[14px] font-700 tracking-[-0.01em] text-ink">
          {item.question}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-ink/40 transition ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="px-4 pb-4 text-[13px] leading-[1.6] text-ink/70">{item.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CtaBottomSection({
  cta,
  c0,
  c1,
  isMobile,
}: {
  cta: { headline: string; urgency: string; cta: string };
  c0: string;
  c1: string;
  isMobile: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section
      className={`relative overflow-hidden ${isMobile ? 'px-5 py-12' : 'px-8 py-16'}`}
      style={{
        background: `radial-gradient(ellipse at 50% 100%, ${c0}, transparent 60%), ${c1}`,
      }}
    >
      <div className="relative mx-auto max-w-[640px] text-center">
        {cta.urgency && (
          <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-white backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            {cta.urgency}
          </span>
        )}
        <h3
          className={`font-display font-800 leading-[1.1] tracking-[-0.025em] text-white ${
            isMobile ? 'text-[22px]' : 'text-[clamp(24px,3.4vw,36px)]'
          }`}
        >
          {cta.headline || 'Prêt à te lancer ?'}
        </h3>
        <form
          onSubmit={(e) => e.preventDefault()}
          className={`mx-auto mt-6 flex w-full max-w-[420px] ${
            isMobile ? 'flex-col gap-2' : 'flex-row gap-2'
          }`}
        >
          <input
            type="email"
            placeholder={t("capture.emailPlaceholderShort")}
            disabled
            className="flex-1 rounded-full border border-white/30 bg-white/15 px-4 py-3 text-[13px] text-white placeholder:text-white/55 backdrop-blur"
          />
          <button
            type="submit"
            className="rounded-full px-5 py-3 text-[13px] font-800 shadow-lg"
            style={{ background: '#fff', color: c1 }}
          >
            {cta.cta || 'Je commence'}
          </button>
        </form>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VideoEmbed — auto-detects YouTube / Vimeo / Loom / Wistia / direct MP4
// ─────────────────────────────────────────────────────────────────────────────

/** Detect the video provider + extract an embeddable URL from a raw user URL. */
export function detectHeroMedia(raw: string): HeroMedia | null {
  const url = raw.trim();
  if (!url) return null;

  // Direct MP4 (data: or http) — caller will decide.
  if (/^data:video\//i.test(url)) return { provider: 'mp4', url };
  if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return { provider: 'mp4', url };

  // YouTube
  const yt =
    url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) {
    return { provider: 'youtube', url: `https://www.youtube.com/embed/${yt[1]}` };
  }

  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (vm) {
    return { provider: 'vimeo', url: `https://player.vimeo.com/video/${vm[1]}` };
  }

  // Loom
  const loom = url.match(/loom\.com\/(?:share|embed)\/([a-f0-9]{24,})/i);
  if (loom) {
    return { provider: 'loom', url: `https://www.loom.com/embed/${loom[1]}` };
  }

  // Wistia
  const wis = url.match(/wistia\.(?:com|net)\/(?:medias|embed)\/([a-z0-9]{8,})/i);
  if (wis) {
    return { provider: 'wistia', url: `https://fast.wistia.net/embed/iframe/${wis[1]}` };
  }

  return null;
}

function VideoEmbed({ media }: { media: HeroMedia }) {
  const { t } = useTranslation();
  if (media.provider === 'mp4') {
    return (
      <video
        src={media.url}
        controls
        playsInline
        className="aspect-video w-full rounded-2xl bg-black object-cover shadow-[0_20px_40px_-16px_rgba(0,0,0,0.45)]"
      />
    );
  }
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-[0_20px_40px_-16px_rgba(0,0,0,0.45)]">
      <iframe
        src={media.url}
        title={t('capture.heroVideo')}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
}

/** Inline editor for the hero video — accepts URL or MP4 upload. */
function HeroMediaEditor({
  media,
  onChange,
}: {
  media: HeroMedia | undefined;
  onChange: (m: HeroMedia | undefined) => void;
}) {
  const { t } = useTranslation();
  const [raw, setRaw] = useState(media?.url ?? '');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => setRaw(media?.url ?? ''), [media?.url]);

  function apply(value: string): void {
    setRaw(value);
    setError(null);
    if (!value.trim()) {
      onChange(undefined);
      return;
    }
    const detected = detectHeroMedia(value);
    if (!detected) {
      setError(t('identity.fields.videoUrlError'));
      return;
    }
    onChange(detected);
  }

  function handleFile(file: File): void {
    if (!file.type.startsWith('video/')) {
      setError(t('identity.fields.videoOnlyVideo'));
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setError(t('identity.fields.videoTooHeavy'));
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      onChange({ provider: 'mp4', url: dataUrl });
      setRaw('(vidéo locale chargée)');
      setUploading(false);
      setError(null);
    };
    reader.onerror = () => {
      setError('Lecture du fichier échouée.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div>
      <span className="mb-1 block text-[11px] font-600 uppercase tracking-[0.1em] text-ink/55">
        {t('capture.heroVideoLabel')}
      </span>
      <div className="flex gap-2">
        <input
          value={raw}
          placeholder={t('capture.videoUrlPlaceholder')}
          onChange={(e) => apply(e.target.value)}
          className="flex-1 rounded-xl border border-ink/[0.10] bg-white px-3 py-2 text-[13px] outline-none focus:border-pink/60 focus:ring-2 focus:ring-pink/15"
        />
        <label className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-xl border border-ink/[0.10] bg-white px-3 py-2 text-[12px] font-700 text-ink/70 transition hover:border-pink/40 hover:text-pink">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          MP4
          <input
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </label>
        {media && (
          <button
            onClick={() => {
              onChange(undefined);
              setRaw('');
              setError(null);
            }}
            className="rounded-xl bg-ink/[0.05] px-3 py-2 text-[12px] text-ink/55 transition hover:bg-pink/10 hover:text-pink"
            title={t("capture.removeVideo")}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {error && <p className="mt-1.5 text-[11px] font-600 text-amber-600">{error}</p>}
      {media && !error && (
        <p className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] font-700 uppercase tracking-[0.1em] text-emerald-600">
          <Check size={10} /> Source détectée : {media.provider}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VariantBEditor — A/B test (only headline + CTA differ between A and B)
// ─────────────────────────────────────────────────────────────────────────────

function VariantBEditor({
  variant,
  fallbackHeadline,
  fallbackCta,
  onChange,
}: {
  variant: VariantB | undefined;
  fallbackHeadline: string;
  fallbackCta: string;
  onChange: (v: VariantB | undefined) => void;
}) {
  const { t } = useTranslation();
  const enabled = variant?.enabled ?? false;

  function toggle(): void {
    if (enabled) {
      onChange({ ...(variant ?? { headline: fallbackHeadline, cta: fallbackCta, enabled: true }), enabled: false });
    } else {
      onChange({
        headline: variant?.headline ?? fallbackHeadline,
        cta: variant?.cta ?? fallbackCta,
        enabled: true,
      });
    }
  }

  return (
    <div className="rounded-2xl border border-violet/25 bg-gradient-to-br from-violet/[0.04] to-pink/[0.03] p-3.5">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-violet to-pink text-white">
            <Sparkles size={12} />
          </span>
          <div>
            <p className="font-display text-[12.5px] font-800 leading-tight tracking-[-0.01em] text-ink">
              A/B Test — Variante B
            </p>
            <p className="text-[11px] text-ink/55">
              {t('identity.variantB.subtitle')}
            </p>
          </div>
        </div>
        <button
          onClick={toggle}
          className={`inline-flex h-6 w-10 shrink-0 items-center rounded-full p-0.5 transition ${
            enabled ? 'bg-gradient-to-r from-violet to-pink' : 'bg-ink/15'
          }`}
        >
          <span
            className={`block h-5 w-5 rounded-full bg-white shadow transition ${
              enabled ? 'translate-x-4' : ''
            }`}
          />
        </button>
      </div>
      {enabled && variant && (
        <div className="space-y-2.5">
          <FieldInput
            label="Accroche (variante B)"
            value={variant.headline}
            onChange={(v) => onChange({ ...variant, headline: v })}
          />
          <FieldInput
            label="Bouton (variante B)"
            value={variant.cta}
            onChange={(v) => onChange({ ...variant, cta: v })}
          />
          <p className="text-[10.5px] font-600 text-ink/45">
            Basculez entre A et B avec les boutons « A » / « B » au-dessus de la preview pour comparer en direct.
          </p>
        </div>
      )}
    </div>
  );
}
